import { Temporal } from 'temporal-polyfill'
import type { OnlineEventViewDto } from '@/api/webs'
import type { WebinarRoomHostLinkDto } from '@/api/webinarRoom'
import type { OptionDto } from '@/types/lookups'
import type { ValidationError } from './errorHandling'
import { sanitizeDateTimeInput } from './dateTimeFormatters'
import { eventZoneInstant } from './timeZones'
import { isHybridMode, isOnDemandMode, isOrbitalXRoom } from './onlineEventMode'
import { parseServerDate } from './time'

/**
 * What the classic app's setup screens say about an OrbitalX-room web (ORB-1086): when viewers can enter,
 * the viewer link, the recording's length and each speaker's host link.
 */

/** Mirrors `WebinarRoomLimits.EntryOpensMinutesBeforeStart`: viewers can enter this long before the start. */
export const ROOM_ENTRY_OPENS_MINUTES_BEFORE_START = 15

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']

const pad = (value: number) => String(value).padStart(2, '0')

/**
 * An event time as its own wall clock, the way the pickers and the wire carry it. The wire labels the
 * wall clock `+00:00` without meaning UTC (ORB-446), so the label is dropped rather than converted.
 */
export function wallClock(value: string | null | undefined): Temporal.PlainDateTime | null {
    const sanitized = sanitizeDateTimeInput(value ?? undefined)
    if (!sanitized) return null
    try {
        return Temporal.PlainDateTime.from(sanitized)
    } catch {
        return null
    }
}

function clockText(hour: number, minute: number): string {
    return `${hour % 12 || 12}:${pad(minute)} ${hour < 12 ? 'AM' : 'PM'}`
}

/** "2:45 PM", on the event's wall clock. */
export function formatWallClockTime(time: Temporal.PlainDateTime): string {
    return clockText(time.hour, time.minute)
}

/** "22 October", on the event's wall clock. */
export function formatWallClockDay(time: Temporal.PlainDateTime): string {
    return `${time.day} ${MONTHS[time.month - 1]}`
}

/** "2 Nov", the date as a re-run's name carries it (the API's "d MMM"). */
export function formatWallClockShortDay(time: Temporal.PlainDateTime): string {
    return `${time.day} ${MONTHS[time.month - 1].slice(0, 3)}`
}

/** When viewers can enter the room, on the event's wall clock, or null without a usable start. */
export function roomEntryOpens(startDateTime: string | null | undefined): Temporal.PlainDateTime | null {
    return wallClock(startDateTime)?.subtract({ minutes: ROOM_ENTRY_OPENS_MINUTES_BEFORE_START }) ?? null
}

/** The time a session ends, on the event's wall clock, or null without a usable end. */
export function sessionEnd(endDateTime: string | null | undefined): Temporal.PlainDateTime | null {
    return wallClock(endDateTime)
}

/**
 * The end that keeps a session's length when its start moves, as `YYYY-MM-DDTHH:mm`. Null when the old
 * start or end is unusable or out of order, so the end is left as the user set it.
 */
export function endKeepingLength(previousStart: string, previousEnd: string, nextStart: string): string | null {
    const from = wallClock(previousStart)
    const to = wallClock(previousEnd)
    const next = wallClock(nextStart)
    if (!from || !to || !next) return null
    const length = from.until(to, { largestUnit: 'minutes' })
    if (length.sign <= 0) return null
    return next.add(length).toString({ smallestUnit: 'minute' })
}

/** The session's length in seconds, from its own wall clock, or null without a usable start and end in order. */
export function sessionLengthSeconds(startDateTime: string | null | undefined, endDateTime: string | null | undefined): number | null {
    const from = wallClock(startDateTime)
    const to = wallClock(endDateTime)
    if (!from || !to) return null
    const seconds = from.until(to, { largestUnit: 'seconds' }).seconds
    return seconds > 0 ? seconds : null
}

/** A session long enough for its recording ends on the next five minutes after the recording does. */
const RECORDING_FIT_STEP_SECONDS = 5 * 60

/**
 * The end, as `YYYY-MM-DDTHH:mm` on the event's wall clock, that makes the session as long as its recording,
 * rounded up to five minutes: the one-click fix for a recording longer than the session. Null without a start.
 */
export function endFittingRecording(startDateTime: string | null | undefined, recordingSeconds: number): string | null {
    const from = wallClock(startDateTime)
    if (!from) return null
    const seconds = Math.ceil(Math.max(0, recordingSeconds) / RECORDING_FIT_STEP_SECONDS) * RECORDING_FIT_STEP_SECONDS
    return from.add({ seconds }).toString({ smallestUnit: 'minute' })
}

/** Whether the session's scheduled end has passed. The end is resolved in the event's own zone. */
export function sessionHasEnded(event: Pick<OnlineEventViewDto, 'EndDateTime' | 'TimeZone'>, now: Date): boolean {
    if (!event.EndDateTime) return false
    const end = eventZoneInstant(event.EndDateTime, event.TimeZone)
    return !!end && end.getTime() <= now.getTime()
}

/**
 * The room's viewer link, once sign-off has set it, or null. Sign-off points the conference join URL at
 * the room page, so it is set exactly when the link is live: not before the first sign-off, and still
 * there after a reopen, while registrants' links keep working.
 */
export function roomViewerLink(event: Pick<OnlineEventViewDto, 'Room' | 'ConferenceJoinUrl'> | null | undefined): string | null {
    if (!isOrbitalXRoom(event)) return null
    return event?.ConferenceJoinUrl?.trim() || null
}

/** Whether the room plays a replay after the session. Mode decides it: Hybrid has one, Upcoming none. */
export function roomHasReplay(event: Pick<OnlineEventViewDto, 'ModeType'>): boolean {
    return isHybridMode(event.ModeType)
}

/**
 * A recording's length as m:ss with the minutes uncapped ("45:00", "75:00"), rounded up to the second, as
 * `WebValidationConstants.RecordingTooLongMessage` writes it, so the End field and the sign-off item agree.
 */
export function formatRecordingLength(seconds: number): string {
    const whole = Math.max(0, Math.ceil(seconds))
    return `${Math.floor(whole / 60)}:${pad(whole % 60)}`
}

/**
 * "The video runs 3:00 of the 45:00 session, leaving 42 minutes for live Q&A after it." The room stays open for
 * questions until the scheduled end, so whoever sets it up hears that the session is not the video's length. Null
 * without a session length, or with less than a minute after the recording.
 */
export function qaAfterVideoLine(recordingSeconds: number, sessionSeconds: number | null): string | null {
    if (sessionSeconds === null) return null
    const leftSeconds = sessionSeconds - Math.ceil(recordingSeconds)
    if (leftSeconds < 60) return null
    const minutes = Math.round(leftSeconds / 60)
    return `The video runs ${formatRecordingLength(recordingSeconds)} of the ${formatRecordingLength(sessionSeconds)} session, `
        + `leaving ${minutes} ${minutes === 1 ? 'minute' : 'minutes'} for live Q&A after it.`
}

/** The sign-off gate's FieldName for everything about the room's recording (`WebValidator`). */
export const RECORDING_FIELD_NAME = 'OnlineEvent.Recording'

export interface EndNote {
    tone: 'warning' | 'help'
    text: string
}

/**
 * What the End field says about a ready recording. The server owns the too-long rule and lists it under
 * the recording's field name; this repeats its words only while the saved times are on screen. Otherwise
 * it splits the session on screen (`sessionSeconds`) into the video and the live Q&A after it, where there
 * is a minute or more of Q&A, and states the length where not. A replacement on its way is the Room tab's.
 */
export function recordingEndNote(
    event: Pick<OnlineEventViewDto, 'Recording' | 'PendingRecording'>,
    outstanding: ValidationError[] | undefined,
    savedTimesOnScreen: boolean,
    sessionSeconds: number | null,
): EndNote | null {
    const recording = event.Recording
    if (recording?.State !== 'Ready' || recording.DurationSeconds == null) return null
    const replacementBusy = !!event.PendingRecording && event.PendingRecording.State !== 'Failed'
    const item = outstanding?.find(error => error.FieldName === RECORDING_FIELD_NAME)
    if (item && savedTimesOnScreen && !replacementBusy) return { tone: 'warning', text: item.Message }
    const split = qaAfterVideoLine(recording.DurationSeconds, sessionSeconds)
    return { tone: 'help', text: split ?? `The recording is ${formatRecordingLength(recording.DurationSeconds)}.` }
}

/**
 * Said when an OrbitalX-room web is being switched to BigMarker: what it has set up for the room stays,
 * unused. Null when there is nothing to keep.
 */
export function roomContentKeptNote(hasRecording: boolean, pollCount: number): string | null {
    const polls = `${pollCount} ${pollCount === 1 ? 'poll' : 'polls'}`
    if (hasRecording && pollCount > 0) return `The recording and ${polls} stay saved, but BigMarker doesn’t use them.`
    if (hasRecording) return 'The recording stays saved, but BigMarker doesn’t use it.'
    if (pollCount > 0) return `The ${polls} ${pollCount === 1 ? 'stays' : 'stay'} saved, but BigMarker doesn’t use ${pollCount === 1 ? 'it' : 'them'}.`
    return null
}

/** The modes an OrbitalX-room web may use: Hybrid and Upcoming. On-demand works as it does today. */
export function roomModeOptions(options: OptionDto[]): OptionDto[] {
    return options.filter(option => !isOnDemandMode(option.Description))
}

/** The mode a new OrbitalX-room web starts on: Hybrid, so it has a replay. Empty until the lookups load. */
export function defaultRoomModeKey(options: OptionDto[]): string {
    return options.find(option => isHybridMode(option.Description))?.Key ?? ''
}

/** "13 Oct, 4:12 PM" in the reader's own time, or "today, 2:14 PM". */
export function formatMoment(at: Date, now: Date): string {
    const sameDay = at.getFullYear() === now.getFullYear() && at.getMonth() === now.getMonth() && at.getDate() === now.getDate()
    const day = sameDay ? 'today' : `${at.getDate()} ${MONTHS[at.getMonth()].slice(0, 3)}`
    return `${day}, ${clockText(at.getHours(), at.getMinutes())}`
}

export interface HostLinkStatus {
    opened: boolean
    text: string
}

/** A speaker's host link, as its card says it: opened, emailed and not opened yet, or not sent. */
export function hostLinkStatus(link: Pick<WebinarRoomHostLinkDto, 'EmailedUtc' | 'OpenedUtc'>, now: Date): HostLinkStatus {
    const opened = parseServerDate(link.OpenedUtc)
    if (opened) return { opened: true, text: `Opened ${formatMoment(opened, now)}` }
    const emailed = parseServerDate(link.EmailedUtc)
    if (emailed) return { opened: false, text: `Emailed ${formatMoment(emailed, now)}. Not opened yet.` }
    return { opened: false, text: 'Not sent yet' }
}
